import axios from "axios";
import {
  getLocalStorageSkedApiToken,
  setLocalStorageSkedApiToken,
  removeLocalStorageSkedApiToken,
} from "libs/storage/adapters";

const MAX_AUTH_RETRIES = 3;
let refreshPromise = null;

/**
 * Shared flag — once any client proves the session is irrecoverably dead,
 * all further auth retries are short-circuited so requests fail fast and
 * the UI can show its normal error/empty states instead of spinning.
 */
let sessionInvalid = false;

/**
 * Create a per-instance retry state object. Each axios client should
 * own its own instance so that concurrent 401s from different clients
 * don't prematurely exhaust the shared counter.
 */
export function createAuthRetryState() {
  return { count: 0 };
}

/**
 * Shared token refresh that deduplicates concurrent refresh attempts.
 * Both skedApi and skedInboxApi interceptors should use this instead
 * of independently calling /api-jwt.
 */
export function refreshToken() {
  if (sessionInvalid) {
    return Promise.reject(
      new Error("Session invalid — skipping token refresh")
    );
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = axios
    .get("/api-jwt")
    .then((response) => {
      const token = response.data.jwt;
      setLocalStorageSkedApiToken(token);
      return token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Get the current token, fetching a new one if none exists.
 */
export async function getToken(forceRequest = false) {
  const token = getLocalStorageSkedApiToken();
  if (token && !forceRequest) {
    return token;
  }
  return refreshToken();
}

/**
 * Handle a 401 error by refreshing the token and retrying the request.
 * Returns the retried request promise, or throws if max retries exceeded.
 *
 * @param {import("axios").AxiosInstance} apiInstance - The axios instance to retry with
 * @param {import("axios").AxiosError} error - The original 401 error
 * @param {string} label - Label for logging (e.g. "skedApi", "skedInboxApi")
 * @param {{ count: number }} retryState - Per-instance retry counter
 */
export async function handleAuthError(apiInstance, error, label, retryState) {
  // Once any client has proven the session is dead, fail immediately.
  if (sessionInvalid) {
    throw error;
  }

  if (retryState.count >= MAX_AUTH_RETRIES) {
    // The token was refreshed successfully MAX_AUTH_RETRIES times and this
    // endpoint still 401s. That is an endpoint-specific authorization denial
    // (e.g. sked-inbox's showInbox gate returning 401 for collaborators who
    // can't access the inbox), NOT a dead session. Fail only this request —
    // do NOT invalidate the shared session or delete the still-valid token,
    // or unrelated skedApi calls (post comments, etc.) would break too.
    console.error(
      `[${label}] Max auth retries (${MAX_AUTH_RETRIES}) exceeded; failing request without invalidating session.`
    );
    throw error;
  }

  retryState.count++;
  console.log(
    `[${label}] Invalid token (attempt ${retryState.count}/${MAX_AUTH_RETRIES})`
  );
  const previousToken = getLocalStorageSkedApiToken();
  removeLocalStorageSkedApiToken();

  try {
    await refreshToken();
  } catch (refreshError) {
    const status = refreshError?.response?.status;
    if (status === 401 || status === 403) {
      // /api-jwt explicitly rejected the session cookie — genuinely dead.
      sessionInvalid = true;
    } else if (previousToken) {
      // Transient failure (e.g. network): restore the token we cleared so
      // concurrent requests aren't left tokenless. The session is not dead,
      // so don't poison it — the retry counter alone gates further attempts.
      setLocalStorageSkedApiToken(previousToken);
    }
    throw error;
  }

  if (!error?.config) throw error;
  return apiInstance(error.config);
}

/**
 * Reset the retry counter for a specific instance. Call on any successful response.
 * Also clears the session-invalid flag since a successful response proves the
 * session is alive (e.g. user logged back in and navigated without a full reload).
 */
export function resetAuthRetryCount(retryState) {
  retryState.count = 0;
  sessionInvalid = false;
}

/**
 * Reset the session-invalid flag so auth retries are possible again.
 * Call on client-side navigation so that page changes give auth a fresh chance.
 */
export function resetSessionState() {
  sessionInvalid = false;
}
